import dayjs from "dayjs";
import {RefObject, useEffect, useRef, useState} from "react";
import {IReportUtilizationAndAvailability} from "../../../../api/types/report";
import {AgGridReact} from "ag-grid-react";
import {useAgGridLocale} from "../../../../../../config/globalConfig";
import {DEFAULT_PLAN_COL_DEF} from "../../../../constants";
import {
    CellClassParams,
    ColDef,
    ColGroupDef,
    HeaderClassParams,
    ICellRendererParams,
    IServerSideGetRowsParams
} from "ag-grid-community";
import AgGridCustomTable from "../../../../../../util/component/AgGridCustomTable";
import "./utilizationAndAvailabilityReport.css";
import {Box} from "@mui/system";
import {useTheme} from "@mui/material";
import {onGridReady} from "../../../../utils";

type Props = {
    result: IReportUtilizationAndAvailability | null,
    currentMonthSelected: boolean
}

const UtilizationAndAvailabilityReport = ({result, currentMonthSelected}: Props) => {
    // Use State
    // REPORT 8
    const [report, setReport] = useState<IReportUtilizationAndAvailability>({
        month: 2,
        tables: []
    });

    // Use Theme
    const theme = useTheme();

    // Use Ref
    const gridRefArray = useRef<RefObject<AgGridReact>[]>([]);

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // Use Effect
    useEffect(() => {
        if (result) setReport({...result, month: result.month - 1});

    }, [result]);

    const defaultColDef = {...DEFAULT_PLAN_COL_DEF, resizable: false, suppressSizeToFit: true};

    const headerColDef = (month: number): ColGroupDef[] => [
        {
            headerName: report ? dayjs().set("month", report?.month).format("MMMM") : "",
            children: [{
                headerName: "",
                field: "name",
                pinned: "left",
                cellRenderer: (params: ICellRendererParams) => params.data.name,
                width: 120
            }]
        },
        {
            children: [{
                headerName: "",
                pinned: "left",
                width: 100
            }]
        },
        {
            children: [{
                headerName: "",
                pinned: "left",
                width: 60
            }]
        },
        ...Array.from(Array(dayjs().month(month).daysInMonth()).keys()).map((day: number) => {
            return {
                headerName: (day + 1).toString(),
                headerClass: (params: HeaderClassParams) => {
                    // logic to return the correct class
                    return dayjs().date() > (day + 1) && currentMonthSelected ? "header-one" : "";
                },
                children: [{
                    headerName: dayjs().month(month).date(day + 1).format("dd").toUpperCase().charAt(0),
                    cellRenderer: (params: ICellRendererParams) => {
                        return params.data?.values[day];
                    },
                    width: 120,
                    headerClass: (params: HeaderClassParams) => {
                        return dayjs().date() > (day + 1) && currentMonthSelected ? "header-one" : "";
                    }
                }]
            };
        }),
        {
            headerName: "TOT",
            children: [
                {
                    headerName: "",
                    cellRenderer: (params: ICellRendererParams) => params.data.tot,
                    width: 120,
                    pinned: "right"
                }
            ]
        }
    ];

    const colDef = (month: number, tableDay: number, requestDate: string): ColDef[] => [
        {
            headerName: dayjs(requestDate).format("DD MMM"),
            pinned: "left",
            width: 120
        },
        {
            headerName: "18:15",
            pinned: "left",
            width: 100,
            cellRenderer: (params: ICellRendererParams) => {
                return params.node.rowIndex !== 3 && params.node.rowIndex !== 5 ? params.data.name : "";
            }
        },
        {
            headerName: "",
            pinned: "left",
            width: 60,
            cellRenderer: (params: ICellRendererParams) => {
                return params.node.rowIndex === 3 || params.node.rowIndex === 5 ? params.data.name : "";
            }
        },
        ...Array.from(Array(dayjs().month(month).daysInMonth()).keys()).map((day: number) => {
            return {
                headerName: (day + 1).toString(),
                headerClass: (params: HeaderClassParams) => dayjs().date() > (day + 1) && currentMonthSelected ? "header-one" : "",
                children: [{
                    headerName: dayjs().month(month).date(day + 1).format("dd").toUpperCase().charAt(0),
                    cellRenderer: (params: ICellRendererParams) => {
                        return (params.node.rowIndex === 3 || params.node.rowIndex === 5) ?
                            (tableDay === 1 || tableDay > (day + 1) ? "" : params.data?.values[day])
                            : (params.node.rowIndex === 6 ? (params.data?.values[day] != null ? params.data?.values[day] + "%" : "-") : params.data?.values[day]);
                    },
                    width: 120,
                    headerClass: (params: HeaderClassParams) => {
                        return dayjs().date() > (day + 1) && currentMonthSelected ? "header-one" : "";
                    },
                    cellStyle: (params: CellClassParams) => ({
                        backgroundColor: params.node.rowIndex === 0 ? (tableDay > (day + 1) ? "" : currentMonthSelected ? theme.palette.secondary.bright : "") :
                            (params.node.rowIndex === 2 || params.node.rowIndex === 4) ?
                                (tableDay <= (day + 1) ? "" : currentMonthSelected ? theme.palette.neutral.light : "")
                                : ""
                    })
                }]
            };
        }),
        ...[1].map(() => ({
            headerName: "TOT",
            children: [
                {
                    headerName: "",
                    cellRenderer: (params: ICellRendererParams) => params.data.tot,
                    width: 120,
                    pinned: "right"
                }
            ]
        }))
    ];

    const createDatasource = (index: number) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                params.success({
                    rowData: report.tables[index].rows,
                    rowCount: report.tables[index].rows.length
                });
            }
        };
    };

    return (
        <>
            {report?.tables?.map((table, index) =>
                <Box my={2}>
                    <AgGridCustomTable
                        // @ts-ignore
                        gridRef={(el: RefObject<AgGridReact>) => gridRefArray.current[index] = el}
                        columnDefs={index === 0 ? headerColDef(report.month) : colDef(report.month, index, table.requestDate)}
                        defaultColDef={defaultColDef}
                        rowSelection="multiple"
                        pagination={false}
                        localeText={agGridLocale}
                        suppressRowClickSelection={true}
                        domLayout="autoHeight"
                        enableRangeSelection={true}
                        enableCellTextSelection={false}
                        rowModelType="serverSide"
                        serverSideInfiniteScroll
                        onGridReady={() =>
                            onGridReady({
                                initialSortColId: "",
                                gridRef: gridRefArray.current[index],
                                createDatasource,
                                index
                            })
                        }
                    />
                </Box>
            )}
        </>
    );
};

export default UtilizationAndAvailabilityReport;